import { useState } from "react"
import { toast } from "sonner"
import { connectBluesky, initiateOAuth } from "@/lib/oauth"
import { getPlatformDisplayName, type OAuthPlatform } from "@/lib/utils"
import { supabase } from "@/integrations/supabase/client"

interface UseOAuthFlowOptions {
  onSuccess?: (platform: OAuthPlatform) => void
  onError?: (error: Error, platform: OAuthPlatform) => void
}

export function useOAuthFlow(options: UseOAuthFlowOptions = {}) {
  const [isConnecting, setIsConnecting] = useState<Record<string, boolean>>({})

  const connectPlatform = async (platform: Exclude<OAuthPlatform, "bluesky">) => {
    setIsConnecting((prev) => ({ ...prev, [platform]: true }))
    try {
      const { authUrl } = await initiateOAuth(platform)
      sessionStorage.setItem("oauth_platform", platform)
      window.location.href = authUrl
    } catch (error) {
      const err = error instanceof Error ? error : new Error(`Failed to connect to ${getPlatformDisplayName(platform)}`)
      toast.error(err.message)
      options.onError?.(err, platform)
      setIsConnecting((prev) => ({ ...prev, [platform]: false }))
    }
  }

  const connectBlueskyAccount = async (handle: string, appPassword: string) => {
    setIsConnecting((prev) => ({ ...prev, bluesky: true }))
    try {
      const result = await connectBluesky(handle, appPassword)
      toast.success(`Bluesky connected: ${result.handle}`)
      options.onSuccess?.("bluesky")
      return result
    } catch (error) {
      const err = error instanceof Error ? error : new Error("Bluesky connection failed")
      toast.error(err.message)
      options.onError?.(err, "bluesky")
      throw err
    } finally {
      setIsConnecting((prev) => ({ ...prev, bluesky: false }))
    }
  }

  const disconnectPlatform = async (platform: OAuthPlatform) => {
    const { data: { user } } = await supabase.auth.getUser()
    if (!user) {
      toast.error("User not authenticated")
      return
    }
    const { error } = await supabase
      .from("social_accounts")
      .delete()
      .eq("user_id", user.id)
      .eq("platform", platform)
    if (error) {
      toast.error(`Failed to disconnect from ${getPlatformDisplayName(platform)}`)
      return
    }
    toast.success(`Disconnected from ${getPlatformDisplayName(platform)}`)
  }

  return {
    connectPlatform,
    connectBlueskyAccount,
    disconnectPlatform,
    isConnecting,
  }
}
